import { collection, doc, documentId, getDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../../firebase';

/** One voice's totals over a date range. */
export interface VoiceUsageRow {
  voiceId: string;
  /** From the voice settings. Null for a voice that was deleted. */
  name: string | null;
  secs: number;
  /** Calls of 10 seconds or more that used this voice. A call that changed voice counts for each. */
  calls: number;
  /** How many times users changed from this voice to another during a call. */
  changedAway: number;
  /** The seconds this voice was active before each change away, added together. */
  changedAwaySecs: number;
}

type DailyStats = Partial<Omit<VoiceUsageRow, 'voiceId' | 'name'>>;

/**
 * Adds up voice_usage_daily/{YYYY-MM-DD} documents, which the voice worker writes at the end
 * of each call (voice-agents/agent/usage.ts). Days are UTC.
 *
 * @param from - First day, YYYY-MM-DD.
 * @param to - Last day, YYYY-MM-DD.
 * @returns Voices sorted by seconds, most used first.
 */
export const getVoiceUsage = async (from: string, to: string): Promise<VoiceUsageRow[]> => {
  const [days, optionsSnap] = await Promise.all([
    getDocs(query(collection(db, 'voice_usage_daily'), where(documentId(), '>=', from), where(documentId(), '<=', to))),
    getDoc(doc(db, 'metadata_private', 'voice_options')),
  ]);
  const voiceOptions = (optionsSnap.data()?.voices ?? []) as { id: string; name: string }[];
  const names = new Map(voiceOptions.map((voice) => [voice.id, voice.name]));

  const rows = new Map<string, VoiceUsageRow>();
  for (const day of days.docs) {
    for (const [voiceId, stats] of Object.entries(day.data() as Record<string, DailyStats>)) {
      const row = rows.get(voiceId)
        ?? { voiceId, name: names.get(voiceId) ?? null, secs: 0, calls: 0, changedAway: 0, changedAwaySecs: 0 };
      row.secs += stats.secs ?? 0;
      row.calls += stats.calls ?? 0;
      row.changedAway += stats.changedAway ?? 0;
      row.changedAwaySecs += stats.changedAwaySecs ?? 0;
      rows.set(voiceId, row);
    }
  }
  return [...rows.values()].sort((a, b) => b.secs - a.secs);
};
